import React, { useEffect, useState } from 'react'
import AdminServiceV2 from '../../../../services/admin.service.v2';
import { Button, Form, Table } from 'react-bootstrap';
import Select from "react-select"

interface IMhcetMapping {
    examId: any
    exam: any
}

export default function MhcetMapping(props: IMhcetMapping) {
    const [apiKey, setApiKey] = useState<string>("");
    const [url, setUrl] = useState<string>("");
    const [shiftOptions, setShiftOptions] = useState<any[]>([]);
    const [vendorSessionOptions, setVendorSessionOptions] = useState<any[]>([]);
    const [existingMappings, setExistingMappings] = useState<any[]>([]);
    const [selectedShift, setSelectedShift] = useState<any>(null);
    const [selectedSession, setSelectedSession] = useState<any>(null);
    const [editingMapping, setEditingMapping] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchVendorSessions = async () => {
        if (!apiKey.trim()) return;
        setFetching(true);
        try {
            const res = await AdminServiceV2.getVendorSessions(props.examId, url, apiKey.trim());
            if (res.status === 200) {
                const sessions = res.data?.data?.sessions?.data || [];
                setVendorSessionOptions(sessions.map((s: any) => ({
                    label: `${s.name} (${s.date} ${s.start_time} - ${s.end_time})`,
                    value: s.id,
                    raw: s
                })));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setFetching(false);
        }
    };

    const getExamMapping = async () => {
        setLoading(true);
        try {
            const res = await AdminServiceV2.getExamMapping(props.examId);
            if (res.status === 200) {
                const raw = res.data?.data?.mappings;
                setExistingMappings(Array.isArray(raw) ? raw : raw ? [raw] : []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveMapping = async () => {
        if (!selectedShift || !selectedSession) return;
        setSaving(true);
        try {
            const payload = {
                shift_id: selectedShift.value,
                session_id: String(selectedSession.value),
                sub_project_id: selectedSession.raw?.sub_project_id ? String(selectedSession.raw.sub_project_id) : undefined
            };

            let res;
            if (editingMapping) {
                res = await AdminServiceV2.updateMhcetMapping(props.examId, editingMapping._id, payload);
            } else {
                res = await AdminServiceV2.createMhcetMapping(props.examId, payload);
            }

            if (res.status === 200 || res.status === 201) {
                setSelectedShift(null);
                setSelectedSession(null);
                setEditingMapping(null);
                getExamMapping();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (m: any) => {
        setEditingMapping(m);
        const shift = shiftOptions.find((s) => s.value === m.shiftId) || { label: m.shiftId, value: m.shiftId };
        const session = vendorSessionOptions.find((s) => String(s.value) === String(m.session_id)) || { label: m.session_id, value: m.session_id };
        setSelectedShift(shift);
        setSelectedSession(session);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancel = () => {
        setEditingMapping(null);
        setSelectedShift(null);
        setSelectedSession(null);
    };

    useEffect(() => {
        if (props.exam) {
            setShiftOptions(props.exam.shifts.map((s: any) => ({
                label: s.shiftName,
                value: s._id,
                raw: s
            })));
            const token = props.exam.cetVendorSlvToken;
            if (token) setApiKey(token);
        }
    }, [props.exam]);

    useEffect(() => {
        getExamMapping();
    }, []);

    return (
        <div className="p-3">
            {/* API Key + Fetch */}
            <Form.Group className="d-flex gap-2 mb-4">
                <Form.Control
                    placeholder="Enter URL"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                />
                <Form.Control
                    placeholder="Enter Vendor API Key / Token"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                />
                <Button onClick={fetchVendorSessions} disabled={fetching || !apiKey.trim() || !url.trim()} style={{ whiteSpace: 'nowrap' }}>
                    {fetching ? "Fetching..." : "Fetch Sessions"}
                </Button>
            </Form.Group>

            {/* Mapping Form */}
            <div className="d-flex gap-3 align-items-end mb-4">
                <Form.Group style={{ flex: 1 }}>
                    <Form.Label>Exam Shift</Form.Label>
                    <Select
                        options={shiftOptions}
                        value={selectedShift}
                        onChange={setSelectedShift}
                        placeholder="Select exam shift..."
                        isClearable
                    />
                </Form.Group>

                <Form.Group style={{ flex: 1 }}>
                    <Form.Label>Vendor Session</Form.Label>
                    <Select
                        options={vendorSessionOptions}
                        value={selectedSession}
                        onChange={setSelectedSession}
                        placeholder={vendorSessionOptions.length ? "Select vendor session..." : "Fetch sessions first"}
                        isClearable
                        isDisabled={vendorSessionOptions.length === 0}
                    />
                </Form.Group>

                <div className="d-flex gap-2 mb-1">
                    <Button
                        onClick={handleSaveMapping}
                        disabled={saving || !selectedShift || !selectedSession}
                    >
                        {saving ? "Saving..." : editingMapping ? "Update Mapping" : "Create Mapping"}
                    </Button>
                    {editingMapping && (
                        <Button variant="outline-secondary" onClick={handleCancel}>
                            Cancel
                        </Button>
                    )}
                </div>
            </div>

            {/* Existing Mappings */}
            <h6>MHCET Mappings</h6>
            {loading ? (
                <p className="text-muted">Loading...</p>
            ) : existingMappings?.length === 0 ? (
                <p className="text-muted">No mappings found.</p>
            ) : (
                <Table bordered hover size="sm">
                    <thead>
                        <tr>
                            <th>Exam Shift</th>
                            <th>Vendor Session</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {existingMappings?.map((m: any, i: number) => {
                            const shiftLabel = shiftOptions.find((s) => s.value === m.shiftId)?.label || m.shiftId;
                            const sessionLabel = vendorSessionOptions.find((s) => String(s.value) === String(m.session_id))?.label || m.session_id;
                            return (
                                <tr key={i} className={editingMapping?._id === m._id ? "table-warning" : ""}>
                                    <td>{shiftLabel}</td>
                                    <td>{sessionLabel}</td>
                                    <td>
                                        <Button size="sm" variant="outline-primary" onClick={() => handleEdit(m)}>
                                            Edit
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
